import React, { useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Space,
  Button,
  Badge,
  Collapse,
  Dropdown,
  Modal,
  InputNumber,
  Input,
  message,
  Popconfirm,
  Pagination,
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  DownOutlined,
  MoreOutlined,
  UserDeleteOutlined,
  UndoOutlined,
  ClockCircleFilled,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { groupMonitorApi, memberActionsApi } from '../services/api';
import { formatBeijingTime } from '../utils/dateUtils';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

const parseUtcDate = (date: string): Date => {
  if (date.includes('T') && !date.includes('Z') && !date.includes('+') && !date.includes('-', 10)) {
    return new Date(date + 'Z');
  }
  if (!date.includes('T') && !date.includes('Z') && !date.includes('+')) {
    return new Date(date.replace(' ', 'T') + 'Z');
  }
  return new Date(date);
};

interface MemberInfo {
  membership_id: number;
  user_id: number;
  username: string | null;
  first_name: string | null;
  telegram_id: number;
  status: string;
  days_joined: number;
  days_remaining: number;
  expire_date: string;
  is_trial: boolean;
  auto_renew: boolean;
}

interface GroupMonitorInfo {
  group_id: number;
  group_name: string;
  telegram_group_id: number;
  member_count: number;
  active_members: number;
  expired_members: number;
  expiring_soon: number;
  members: MemberInfo[];
}

const GroupMonitor: React.FC = () => {
  const queryClient = useQueryClient();
  const [extendModalVisible, setExtendModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MemberInfo | null>(null);
  const [extendDays, setExtendDays] = useState<number>(30);
  const [actionReason, setActionReason] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');

  // 获取统计数据
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['group-monitor-stats'],
    queryFn: groupMonitorApi.getStats,
    refetchInterval: 30000,
  });

  // 获取群组监控数据
  const { data: groupsMonitor, isLoading: monitorLoading, refetch: refetchMonitor } = useQuery({
    queryKey: ['groups-monitor', page, pageSize, searchText],
    queryFn: () => groupMonitorApi.getGroupsMonitor({ page, pageSize, search: searchText || undefined }),
    refetchInterval: 30000,
  });

  const statsData = (stats as any) || {};
  const groupsData = (groupsMonitor as any)?.items || [];
  const totalGroups = (groupsMonitor as any)?.total || 0;

  // 踢出会员
  const kickMutation = useMutation({
    mutationFn: (data: { membership_id: number; reason?: string }) =>
      memberActionsApi.kickMember(data),
    onSuccess: () => {
      message.success('已成功踢出会员');
      queryClient.invalidateQueries({ queryKey: ['groups-monitor'] });
      queryClient.invalidateQueries({ queryKey: ['group-monitor-stats'] });
    },
    onError: (error: any) => {
      message.error(`踢出失败: ${error.response?.data?.detail || error.message}`);
    },
  });

  // 恢复会员
  const restoreMutation = useMutation({
    mutationFn: (data: { membership_id: number; reason?: string }) =>
      memberActionsApi.restoreMember(data),
    onSuccess: () => {
      message.success('已恢复会员状态');
      queryClient.invalidateQueries({ queryKey: ['groups-monitor'] });
      queryClient.invalidateQueries({ queryKey: ['group-monitor-stats'] });
    },
    onError: (error: any) => {
      message.error(`恢复失败: ${error.response?.data?.detail || error.message}`);
    },
  });

  // 延期会员
  const extendMutation = useMutation({
    mutationFn: (data: { membership_id: number; extend_days: number }) =>
      memberActionsApi.extendMembership(data),
    onSuccess: () => {
      message.success('已延长会员有效期');
      setExtendModalVisible(false);
      setSelectedMember(null);
      setExtendDays(30);
      queryClient.invalidateQueries({ queryKey: ['groups-monitor'] });
      queryClient.invalidateQueries({ queryKey: ['group-monitor-stats'] });
    },
    onError: (error: any) => {
      message.error(`延期失败: ${error.response?.data?.detail || error.message}`);
    },
  });

  // 暂停会员
  const suspendMutation = useMutation({
    mutationFn: (data: { membership_id: number; reason?: string }) =>
      memberActionsApi.suspendMember(data),
    onSuccess: () => {
      message.success('已暂停会员');
      queryClient.invalidateQueries({ queryKey: ['groups-monitor'] });
      queryClient.invalidateQueries({ queryKey: ['group-monitor-stats'] });
    },
    onError: (error: any) => {
      message.error(`暂停失败: ${error.response?.data?.detail || error.message}`);
    },
  });

  // 操作菜单
  const getMemberActions = (member: MemberInfo) => [
    {
      key: 'extend',
      icon: <ClockCircleFilled />,
      label: '延长有效期',
      onClick: () => {
        setSelectedMember(member);
        setExtendModalVisible(true);
      },
    },
    {
      key: 'kick',
      icon: <UserDeleteOutlined />,
      label: '踢出群组',
      danger: true,
      onClick: () => {
        Modal.confirm({
          title: '确认踢出会员',
          content: `确定要踢出 ${member.username || member.first_name} 吗？`,
          okText: '确认',
          cancelText: '取消',
          okType: 'danger',
          onOk: () => kickMutation.mutate({ membership_id: member.membership_id }),
        });
      },
    },
    member.status === 'expired' && {
      key: 'restore',
      icon: <UndoOutlined />,
      label: '恢复状态',
      onClick: () => {
        restoreMutation.mutate({ membership_id: member.membership_id });
      },
    },
    member.status === 'active' && {
      key: 'suspend',
      icon: <PauseCircleOutlined />,
      label: '暂停会员',
      onClick: () => {
        Modal.confirm({
          title: '确认暂停会员',
          content: `确定要暂停 ${member.username || member.first_name} 的会员吗？`,
          okText: '确认',
          cancelText: '取消',
          onOk: () => suspendMutation.mutate({ membership_id: member.membership_id }),
        });
      },
    },
  ].filter(Boolean);

  // 成员表格列配置
  const memberColumns = [
    {
      title: '用户',
      key: 'user',
      render: (_: any, record: MemberInfo) => (
        <div>
          <div className="font-medium">
            {record.username ? `@${record.username}` : record.first_name || '未知用户'}
          </div>
          <Text type="secondary" className="text-xs">
            ID: {record.telegram_id}
          </Text>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: MemberInfo) => {
        const statusMap: any = {
          'active': { color: 'success', text: '活跃' },
          'expired': { color: 'default', text: '已过期' },
          'suspended': { color: 'warning', text: '已暂停' },
          'cancelled': { color: 'error', text: '已取消' },
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return (
          <Space>
            <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
            {record.is_trial && <Tag color="blue">试用</Tag>}
            {record.auto_renew && <Tag color="green">自动续费</Tag>}
          </Space>
        );
      },
    },
    {
      title: '已加入天数',
      dataIndex: 'days_joined',
      key: 'days_joined',
      render: (days: number) => <Text strong>{days} 天</Text>,
      sorter: (a: MemberInfo, b: MemberInfo) => a.days_joined - b.days_joined,
    },
    {
      title: '剩余天数',
      dataIndex: 'days_remaining',
      key: 'days_remaining',
      render: (days: number) => {
        let color = 'success';
        if (days <= 0) color = 'default';
        else if (days <= 3) color = 'error';
        else if (days <= 7) color = 'warning';

        return (
          <Tag color={color}>
            {days > 0 ? `${days} 天` : '已过期'}
          </Tag>
        );
      },
      sorter: (a: MemberInfo, b: MemberInfo) => a.days_remaining - b.days_remaining,
    },
    {
      title: '到期时间',
      dataIndex: 'expire_date',
      key: 'expire_date',
      render: (date: string) => date ? formatBeijingTime(date) : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: any, record: MemberInfo) => (
        <Dropdown
          menu={{ items: getMemberActions(record) }}
          trigger={['click']}
        >
          <Button
            type="default"
            icon={<MoreOutlined />}
            size="middle"
          >
            操作
          </Button>
        </Dropdown>
      ),
    },
  ];

  const handleRefresh = () => {
    refetchStats();
    refetchMonitor();
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <Title level={2} className="mb-2">
            群聊监控
          </Title>
          <Text type="secondary">实时监控各个会员圈的成员状态和有效期</Text>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={statsLoading || monitorLoading}
        >
          刷新数据
        </Button>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总群组数"
              value={statsData.total_groups || 0}
              prefix={<TeamOutlined className="text-blue-500" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总会员数"
              value={statsData.total_members || 0}
              prefix={<UserOutlined className="text-purple-500" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="活跃会员"
              value={statsData.active_members || 0}
              prefix={<CheckCircleOutlined className="text-green-500" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="即将过期"
              value={statsData.expiring_soon || 0}
              prefix={<WarningOutlined className="text-orange-500" />}
              suffix={<Text type="secondary" className="text-sm">(7天内)</Text>}
            />
          </Card>
        </Col>
      </Row>

      {/* 搜索和分页控制 */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <Input.Search
            placeholder="搜索群组名称"
            allowClear
            style={{ width: 250 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={() => setPage(1)}
          />
          <Text type="secondary">共 {totalGroups} 个群组</Text>
        </div>

        {/* 群组监控列表 - 使用折叠面板 */}
        <Collapse
          accordion={false}
          expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
          className="bg-transparent"
        >
        {groupsData.map((group: GroupMonitorInfo) => (
          <Panel
            key={group.group_id}
            header={
              <div className="flex items-center justify-between w-full pr-4">
                <Space>
                  <TeamOutlined />
                  <span className="font-medium">{group.group_name}</span>
                  <Tag color="blue">ID: {group.telegram_group_id}</Tag>
                </Space>
                <Space>
                  <Badge count={group.member_count} showZero color="blue">
                    <span className="px-2 text-gray-600">总成员</span>
                  </Badge>
                  <Badge count={group.active_members} showZero color="green">
                    <span className="px-2 text-gray-600">活跃</span>
                  </Badge>
                  <Badge count={group.expiring_soon} showZero color="orange">
                    <span className="px-2 text-gray-600">即将过期</span>
                  </Badge>
                  <Badge count={group.expired_members} showZero>
                    <span className="px-2 text-gray-600">已过期</span>
                  </Badge>
                </Space>
              </div>
            }
          >
            {/* 群组统计 */}
            <div className="mb-4">
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="总成员"
                    value={group.member_count}
                    prefix={<UserOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="活跃会员"
                    value={group.active_members}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="即将过期"
                    value={group.expiring_soon}
                    valueStyle={{ color: '#fa8c16' }}
                    prefix={<ClockCircleOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="已过期"
                    value={group.expired_members}
                    valueStyle={{ color: '#8c8c8c' }}
                  />
                </Col>
              </Row>
            </div>

            {/* 成员列表 */}
            <Table
              columns={memberColumns}
              dataSource={group.members}
              rowKey="membership_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 名成员`,
              }}
              loading={monitorLoading}
              size="small"
            />
          </Panel>
        ))}
      </Collapse>

      {/* 分页 */}
      {totalGroups > 0 && (
        <div className="flex justify-end mt-4">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={totalGroups}
            showSizeChanger
            showQuickJumper
            showTotal={(total) => `共 ${total} 个群组`}
            onChange={(p, ps) => {
              setPage(p);
              setPageSize(ps);
            }}
          />
        </div>
      )}
      </Card>

      {groupsData.length === 0 && !monitorLoading && (
        <Card>
          <div className="text-center py-8">
            <TeamOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
            <div className="mt-4 text-gray-500">暂无群组数据</div>
          </div>
        </Card>
      )}

      {/* 延期弹窗 */}
      <Modal
        title="延长会员有效期"
        open={extendModalVisible}
        onOk={() => {
          if (selectedMember) {
            extendMutation.mutate({
              membership_id: selectedMember.membership_id,
              extend_days: extendDays,
            });
          }
        }}
        onCancel={() => {
          setExtendModalVisible(false);
          setSelectedMember(null);
          setExtendDays(30);
        }}
        confirmLoading={extendMutation.isPending}
      >
        <div className="space-y-4 py-4">
          <div>
            <Text strong>用户: </Text>
            <Text>{selectedMember?.username || selectedMember?.first_name}</Text>
          </div>
          <div>
            <Text strong>当前到期时间: </Text>
            <Text>
              {selectedMember?.expire_date
                ? formatBeijingTime(selectedMember.expire_date)
                : '-'}
            </Text>
          </div>
          <div>
            <Text strong className="block mb-2">延长天数: </Text>
            <InputNumber
              min={1}
              max={3650}
              value={extendDays}
              onChange={(value) => setExtendDays(value || 30)}
              addonAfter="天"
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <Text strong>新到期时间: </Text>
            <Text type="success">
              {selectedMember?.expire_date
                ? formatBeijingTime(
                    new Date(
                      parseUtcDate(selectedMember.expire_date).getTime() +
                        extendDays * 24 * 60 * 60 * 1000
                    )
                  )
                : '-'}
            </Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GroupMonitor;
